import type { PersonaKey } from '@/constants/avatars';
import type { LearnerState } from '@/types/learner';
import type { CheckInBeat, Lesson } from '@/types/lesson';
import type { ScenarioConfig } from '@/types/scenario';
import type { SessionRecord } from '@/types/session';
import type { CBStartData } from './types';

/**
 * Pure helpers that build the mode-specific startData payloads passed to
 * `mountAvatar`. Keeping these in one file means every screen's payload is
 * derived identically and can be diffed against the brief §7 / CB spec §7.
 */

export function buildGreetingStartData(
  learner: LearnerState,
): Extract<CBStartData, { mode: 'greeting' }> {
  const last = learner.session_history[0];
  return {
    mode: 'greeting',
    learner: {
      name: learner.display_name,
      current_module: learner.current_module,
      current_lesson: learner.current_lesson,
    },
    last_session: last
      ? {
          persona_id: last.persona_id,
          outcome: last.outcome,
          score: last.score,
          attempt_number: last.attempt_number,
        }
      : null,
    saved_playbook: learner.saved_playbook.slice(0, 3).map((m) => ({
      quote: m.quote,
      persona_id: personaForSession(m.session_id, learner),
      tags: m.tags,
    })),
    strengths: learner.strengths.map((s) => s.skill),
    focus_areas: learner.focus_areas.map((s) => s.skill),
  };
}

function personaForSession(sessionId: string, learner: LearnerState): PersonaKey {
  return learner.session_history.find((s) => s.id === sessionId)?.persona_id ?? 'dan';
}

/**
 * Mode B startData for the lesson mount. We use the first beat's id as the
 * initial context; subsequent beat advances are communicated to the coach
 * through `ask()` calls, not by re-mounting (see CB spec §2 Mode B).
 */
export function buildLessonStartData(
  learner: LearnerState,
  lesson: Lesson,
): Extract<CBStartData, { mode: 'lesson' }> {
  return {
    mode: 'lesson',
    learner: {
      name: learner.display_name,
      current_module: learner.current_module,
      current_lesson: learner.current_lesson,
    },
    beat_id: lesson.beats[0]?.id ?? lesson.lesson_id,
  };
}

/**
 * Mode C startData — used when the avatar should be reconfigured for a
 * check-in beat (rarely in MVP; most of the time we mount with Mode B and
 * evaluate check-ins client-side against the same rubric shape).
 */
export function buildCheckInStartData(
  learner: LearnerState,
  beat: CheckInBeat,
): Extract<CBStartData, { mode: 'check_in' }> {
  return {
    mode: 'check_in',
    learner: {
      name: learner.display_name,
      current_module: learner.current_module,
      current_lesson: learner.current_lesson,
    },
    beat_id: beat.id,
    beat_rubric: beat.rubric,
  };
}

/**
 * Mode (debrief) startData — the coach on the DebriefScreen. Composes the
 * verdict (score, outcome, hits/misses, moments). The narrative the coach
 * actually speaks on "Play full debrief" is composed front-end-side and
 * pushed via `playResponse()` — the CB-side debrief dialog flow MUST stay
 * silent on mount (single-source rule, see feedback memory).
 *
 * The full session transcript is NOT included here on purpose: startData
 * is serialized into the loader script-tag URL, and a long transcript
 * blows past the ~8KB browser/CDN URL cap. Loader returns 404 and the coach
 * never mounts. The debrief screen reads the transcript directly from
 * the SessionRecord in memory; the dialog flow doesn't need it because
 * the coach stays silent on mount.
 */
export function buildDebriefStartData(
  learner: LearnerState,
  session: SessionRecord,
  scenario: ScenarioConfig,
): Extract<CBStartData, { mode: 'debrief' }> {
  const milestones_missed = scenario.milestones.filter((m) => !session.milestones_hit.includes(m));
  return {
    mode: 'debrief',
    learner: {
      name: learner.display_name,
      current_module: learner.current_module,
      current_lesson: learner.current_lesson,
    },
    verdict: {
      score: session.score,
      outcome: session.outcome,
      milestones_hit: session.milestones_hit,
      milestones_missed,
      moments: session.moments.map((m) => {
        const out: { type: 'win' | 'growth'; quote: string; at_s: number; coach_note_id?: string } =
          {
            type: m.type,
            quote: m.quote,
            at_s: m.at_s,
          };
        if (m.coach_note_id !== undefined) out.coach_note_id = m.coach_note_id;
        return out;
      }),
    },
  };
}

/**
 * Mode (playbook) startData — the coach on the PlaybookScreen. Carries the saved
 * moments so the dialog flow CAN reference a specific play if the learner asks
 * a follow-up ("what did I say in the first one?"). The narratives the coach
 * actually speaks (the "Play all my wins" walkthrough and each per-card
 * "Hear the coach's take") are composed front-end-side and pushed via
 * `playResponse()` — the CB-side playbook dialog flow MUST stay silent on
 * mount (single-source rule, see feedback memory).
 */
export function buildPlaybookStartData(
  learner: LearnerState,
): Extract<CBStartData, { mode: 'playbook' }> {
  const uniqueSkills = new Set<string>();
  learner.saved_playbook.forEach((m) => m.tags.forEach((t) => uniqueSkills.add(t)));
  return {
    mode: 'playbook',
    learner: {
      name: learner.display_name,
      current_module: learner.current_module,
      current_lesson: learner.current_lesson,
    },
    // startData is serialized into the loader script-tag URL (~8KB cap). The
    // saved playbook is unbounded and each quote is a full agent paragraph, so
    // mapping all of them verbatim blows the cap → loader 404s → the coach
    // never mounts (exactly the failure the debrief builder avoids by omitting
    // the transcript). Cap to the most recent few and truncate each quote; the
    // dialog flow only needs enough to recognize a play, and the spoken
    // narration is composed front-end-side via playResponse() anyway.
    saved_moments: learner.saved_playbook.slice(0, 6).map((m) => ({
      quote: m.quote.length > 120 ? `${m.quote.slice(0, 117)}…` : m.quote,
      persona_id: personaForSession(m.session_id, learner),
      tags: m.tags,
    })),
    total_plays: learner.saved_playbook.length,
    unique_skills: uniqueSkills.size,
  };
}

/**
 * Mode (roleplay) startData — passed to the buyer-persona avatar on mount.
 * Includes prior attempts and skill signals so the persona's dialog flow
 * can branch personality (CB spec §3 "stretch goal": tailor the persona's opening
 * coldness to the learner's focus areas).
 */
export function buildRoleplayStartData(
  learner: LearnerState,
  personaId: PersonaKey,
): Extract<CBStartData, { mode: 'roleplay' }> {
  const personaAttempts = learner.session_history.filter((s) => s.persona_id === personaId);
  const last = personaAttempts[0];
  return {
    mode: 'roleplay',
    learner: {
      name: learner.display_name,
      current_module: learner.current_module,
      current_lesson: learner.current_lesson,
    },
    prior_attempts: personaAttempts.length,
    last_attempt_score: last?.score ?? null,
    last_attempt_outcome: last?.outcome ?? null,
    strengths: learner.strengths.map((s) => s.skill),
    focus_areas: learner.focus_areas.map((s) => s.skill),
  };
}
